import { api } from "../../api"
import { makeHourReporterMembershipViewModel } from "./hour-reporter-membership"
import { makeHourReportAuditableMembershipViewModel } from "./hour-report-auditable-membership"
import { makeMembershipViewModel } from "./membership"
import {
  HourReportAdminAssignmentOptionsResponseSchema,
  HourReportAdminsResponseSchema,
  HourReportAuditableMembershipResponseSchema,
  HourReporterMembershipResponseSchema,
  HourReportRoleAssignmentOptionsResponseSchema,
  MembershipResponseSchema,
} from "./schemas"
import type { HourReporterMembershipViewModel } from "./hour-reporter-membership"
import type { HourReportAuditableMembershipViewModel } from "./hour-report-auditable-membership"
import type { MembershipViewModel } from "./membership"
import type {
  HourReportAdmin,
  HourReportAdminAssignmentOption,
  HourReportAdminsResponse,
  HourReportRoleAssignmentOption,
} from "./schemas"

const apiEndpoints = {
  getOrgMemberships: (orgId: string) => `/orgs/${orgId}/memberships`,
  getUserMemberships: (userId: string) => `/users/${userId}/memberships`,
  getHourReporterMemberships: (orgId: string) => `/orgs/${orgId}/hour-reporter-memberships`,
  orgHourReportAuditableMemberships: (orgId: string) => `/orgs/${orgId}/hour-report-auditable-memberships`,
  orgHourReportRoleAssignmentOptions: (orgId: string) => `/orgs/${orgId}/hour-report-role-assignment-options`,
  orgHourReportAdmins: (orgId: string) => `/orgs/${orgId}/hour-report-admins`,
  orgHourReportAdminAssignmentOptions: (orgId: string) => `/orgs/${orgId}/hour-report-admin-assignment-options`,
  updateHourReportCheckers: (membershipId: string) => `/memberships/${membershipId}/hour-report-checkers`,
  updateHourReportApprovers: (membershipId: string) => `/memberships/${membershipId}/hour-report-approvers`,
}

export type UpdateHourReportCheckersData = {
  id: string
  orgId: string
  checkerMembershipIdsToAdd?: string[]
  checkerMembershipIdsToRemove?: string[]
}

export type UpdateHourReportApproversData = {
  id: string
  orgId: string
  approverMembershipIdsToAdd?: string[]
  approverMembershipIdsToRemove?: string[]
}

export type UpdateHourReportAdminsData = {
  orgId: string
  adminMembershipIdsToAdd: string[]
  adminMembershipIdsToRemove: string[]
}

export const membershipApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrgMemberships: builder.query<MembershipViewModel[], string>({
      query: (orgId: string) => apiEndpoints.getOrgMemberships(orgId),
      transformResponse: (response) => {
        return MembershipResponseSchema.array().parse(response).map(makeMembershipViewModel)
      },
    }),
    getUserMemberships: builder.query<MembershipViewModel[], string>({
      query: (userId: string) => apiEndpoints.getUserMemberships(userId),
      transformResponse: (response) => {
        return MembershipResponseSchema.array().parse(response).map(makeMembershipViewModel)
      },
    }),
    getHourReporterMemberships: builder.query<HourReporterMembershipViewModel[], string>({
      query: (orgId: string) => apiEndpoints.getHourReporterMemberships(orgId),
      transformResponse: (response) => {
        return HourReporterMembershipResponseSchema.array().parse(response).map(makeHourReporterMembershipViewModel)
      },
      providesTags: ["HourReporterMembership"],
    }),
    getOrgHourReportAuditableMemberships: builder.query<HourReportAuditableMembershipViewModel[], string>({
      query: (orgId: string) => apiEndpoints.orgHourReportAuditableMemberships(orgId),
      transformResponse: (response) => {
        return HourReportAuditableMembershipResponseSchema.array()
          .parse(response)
          .map(makeHourReportAuditableMembershipViewModel)
      },
      providesTags: ["HourReportAuditableMembership"],
    }),
    getHourReportRoleAssignmentOptions: builder.query<HourReportRoleAssignmentOption[], string>({
      query: (orgId: string) => apiEndpoints.orgHourReportRoleAssignmentOptions(orgId),
      transformResponse: (response) => {
        return HourReportRoleAssignmentOptionsResponseSchema.parse(response).options
      },
    }),
    getHourReportAdmins: builder.query<HourReportAdminsResponse, string>({
      query: (orgId: string) => apiEndpoints.orgHourReportAdmins(orgId),
      transformResponse: (response) => {
        return HourReportAdminsResponseSchema.parse(response)
      },
      providesTags: ["HourReportAdmins"],
    }),
    getHourReportAdminAssignmentOptions: builder.query<HourReportAdminAssignmentOption[], string>({
      query: (orgId: string) => apiEndpoints.orgHourReportAdminAssignmentOptions(orgId),
      transformResponse: (response) => {
        return HourReportAdminAssignmentOptionsResponseSchema.parse(response).options
      },
      providesTags: ["HourReportAdmins"],
    }),
    updateHourReportAdmins: builder.mutation<void, UpdateHourReportAdminsData>({
      query: ({ orgId, ...updateData }) => ({
        url: apiEndpoints.orgHourReportAdmins(orgId),
        method: "PATCH",
        body: updateData,
      }),
      async onQueryStarted(
        { orgId, adminMembershipIdsToAdd, adminMembershipIdsToRemove },
        { dispatch, queryFulfilled }
      ) {
        const adminsToAdd: HourReportAdmin[] = []
        const assignmentOptionsPatch = dispatch(
          membershipApi.util.updateQueryData("getHourReportAdminAssignmentOptions", orgId, (draft) => {
            draft.forEach((option) => {
              if (adminMembershipIdsToAdd.includes(option.id)) {
                option.isHourReportAdmin = true
                adminsToAdd.push({ id: option.id, name: option.name, email: option.email, orgId: option.orgId })
              }

              if (adminMembershipIdsToRemove.includes(option.id)) {
                option.isHourReportAdmin = false
              }
            })
          })
        )
        const adminsPatch = dispatch(
          membershipApi.util.updateQueryData("getHourReportAdmins", orgId, (draft) => {
            adminMembershipIdsToRemove.forEach((membershipId) => {
              const index = draft.admins.findIndex((admin) => admin.id === membershipId)
              if (index !== -1) draft.admins.splice(index, 1)
            })

            adminsToAdd.forEach((admin) => {
              if (!draft.admins.some((item) => item.id === admin.id)) draft.admins.push(admin)
            })
          })
        )

        try {
          await queryFulfilled
        } catch {
          adminsPatch.undo()
          assignmentOptionsPatch.undo()
        }
      },
      invalidatesTags: (_result, error) => (error ? [] : ["HourReportAdmins"]),
    }),
    updateHourReportCheckers: builder.mutation<void, UpdateHourReportCheckersData>({
      query: ({ id, orgId, ...updateData }) => ({
        url: apiEndpoints.updateHourReportCheckers(id),
        method: "PATCH",
        body: updateData,
      }),
      async onQueryStarted(
        { id, orgId, checkerMembershipIdsToAdd = [], checkerMembershipIdsToRemove = [] },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          membershipApi.util.updateQueryData("getHourReporterMemberships", orgId, (draft) => {
            const membership = draft.find((item) => item.id === id)
            if (!membership) return

            checkerMembershipIdsToRemove.forEach((membershipIdToRemove) => {
              membership.checkers = membership.checkers.filter((item) => item.id !== membershipIdToRemove)
            })

            checkerMembershipIdsToAdd.forEach((membershipIdToAdd) => {
              if (membership.checkers.some((item) => item.id === membershipIdToAdd)) return

              const roleMembership = draft.find((item) => item.id === membershipIdToAdd)
              if (roleMembership) {
                membership.checkers.push({
                  id: roleMembership.id,
                  firstname: roleMembership.firstname,
                  lastname: roleMembership.lastname,
                  fullname: roleMembership.fullname,
                  userEmail: roleMembership.userEmail,
                  role: roleMembership.role,
                  status: roleMembership.status,
                  invitationStatus: roleMembership.invitationStatus,
                  orgId: roleMembership.orgId,
                })
              }
            })
          })
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: ["HourReporterMembership", "HourReportAuditableMembership"],
    }),
    updateHourReportApprovers: builder.mutation<void, UpdateHourReportApproversData>({
      query: ({ id, orgId, ...updateData }) => ({
        url: apiEndpoints.updateHourReportApprovers(id),
        method: "PATCH",
        body: updateData,
      }),
      async onQueryStarted(
        { id, orgId, approverMembershipIdsToAdd = [], approverMembershipIdsToRemove = [] },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          membershipApi.util.updateQueryData("getHourReporterMemberships", orgId, (draft) => {
            const membership = draft.find((item) => item.id === id)
            if (!membership) return

            approverMembershipIdsToRemove.forEach((membershipIdToRemove) => {
              membership.approvers = membership.approvers.filter((item) => item.id !== membershipIdToRemove)
            })

            approverMembershipIdsToAdd.forEach((membershipIdToAdd) => {
              if (membership.approvers.some((item) => item.id === membershipIdToAdd)) return

              const roleMembership = draft.find((item) => item.id === membershipIdToAdd)
              if (roleMembership) {
                membership.approvers.push({
                  id: roleMembership.id,
                  firstname: roleMembership.firstname,
                  lastname: roleMembership.lastname,
                  fullname: roleMembership.fullname,
                  userEmail: roleMembership.userEmail,
                  role: roleMembership.role,
                  status: roleMembership.status,
                  invitationStatus: roleMembership.invitationStatus,
                  orgId: roleMembership.orgId,
                })
              }
            })
          })
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: ["HourReporterMembership", "HourReportAuditableMembership"],
    }),
  }),
})

export const {
  useGetOrgMembershipsQuery,
  useGetUserMembershipsQuery,
  useGetHourReporterMembershipsQuery,
  useGetOrgHourReportAuditableMembershipsQuery,
  useGetHourReportRoleAssignmentOptionsQuery,
  useGetHourReportAdminsQuery,
  useGetHourReportAdminAssignmentOptionsQuery,
  useUpdateHourReportAdminsMutation,
  useUpdateHourReportCheckersMutation,
  useUpdateHourReportApproversMutation,
} = membershipApi
